import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin'
import { decodeBase64, encodeBase64 } from '@/service/uiUtils.js'

function isMissingKeyError(err) {
    return err?.message === 'Item with given key does not exist'
}

function logStorageError(key, err) {
    if (isMissingKeyError(err)) return
    console.log(key, err)
}

class CapacitorSecureStorage {
    async setItem(key, value) {
        try {
            await SecureStoragePlugin.set({ key, value })
        } catch (err) {
            logStorageError(key, err)
        }
    }

    async getItem(key) {
        try {
            const { value } = await SecureStoragePlugin.get({ key })
            return value
        } catch (err) {
            logStorageError(key, err)
            return null
        }
    }

    async removeItem(key) {
        try {
            await SecureStoragePlugin.remove({ key })
        } catch (err) {
            logStorageError(key, err)
        }
    }

    async clear() {
        if (SecureStoragePlugin.clear) await SecureStoragePlugin.clear()
    }
}

/**
 * Get an item by key.
 * @param {string} key
 * @returns {Promise<string|null>} resolves to the stored value, or null if missing
 */
async function getItem(key) {
    try {
        const { value } = await SecureStoragePlugin.get({ key })
        return value
    } catch (err) {
        logStorageError(key, err)
        return null
    }
}

/**
 * Get an item object by key.
 * @param {string} key
 * @param {boolean} withB64
 * @returns {Promise} resolves to the stored value, or null if missing
 */
async function getItemObj(key, withB64) {
    try {
        const { value } = await SecureStoragePlugin.get({ key })
        return JSON.parse(withB64 ? decodeBase64(value) : value)
    } catch (err) {
        logStorageError(key, err)
        return null
    }
}

/**
 * Store a string value under the given key.
 * @param {string} key
 * @param {string} value
 * @returns {Promise<void>}
 */
async function setItem(key, value) {
    try {
        await SecureStoragePlugin.set({ key, value })
    } catch (err) {
        logStorageError(key, err)
    }
}

/**
 * Store a string value under the given key.
 * @param {string} key
 * @param {object} value
 * @param {boolean} withB64
 * @returns {Promise<void>}
 */
async function setItemObj(key, value, withB64) {
    try {
        const str = JSON.stringify(value)
        const v = withB64 ? encodeBase64(str) : str
        await SecureStoragePlugin.set({ key, value: v })
    } catch (err) {
        logStorageError(key, err)
    }
}

/**
 * Remove the value stored under the given key.
 * @param {string} key
 * @returns {Promise<void>}
 */
async function removeItem(key) {
    try {
        await SecureStoragePlugin.remove({ key })
    } catch (err) {
        logStorageError(key, err)
    }
}

export default {
    CapacitorSecureStorage,
    getItem,
    getItemObj,
    setItem,
    setItemObj,
    removeItem,
}
